import React from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import RootShell from "./RootShell";
import { LEGACY_ADMIN_REDIRECTS } from "./paths";

// ---- redesigned pages ----
import HomePage from "../pages/HomePage";
import PermalinkPage from "../pages/PermalinkPage";
import SeriesPage from "../pages/SeriesPage";
import SearchResultsPage from "../pages/SearchResultsPage";

// ---- public pages (legacy components, unchanged) ----
import Signup from "../../pages/signupLogin/Signup";
import Signin from "../../pages/signupLogin/Signin";
import ForgetPassword from "../../pages/signupLogin/ForgetPassword";
import ContactPage from "../pages/ContactPage";
import AboutPage from "../pages/AboutPage";
import LivePage from "../pages/LivePage";
import DonationPage from "../pages/DonationPage";
import DonationPageMobile from "../../pages/donationPage/DonationPageMobile";
import ChaiClubPage from "../pages/ChaiClubPage";
import AskQuestionPage from "../pages/AskQuestionPage";
import PrivacyPolicyPage from "../pages/PrivacyPolicyPage";
import DailyShiurPage from "../pages/DailyShiurPage";
import ParshaPage from "../pages/ParshaPage";
import MonthlyClassesPage from "../pages/MonthlyClassesPage";
import LatestClassesPage from "../pages/LatestClassesPage";
import ProfilePage from "../../pages/profilePage/ProfilePage";
import ConceptPage from "../../pages/conceptPage/ConceptPage";
import EmbeddableVideo from "../../ReuseableComponents/EmbeddableVideo";
import EmbeddableAudio from "../../ReuseableComponents/EmbeddableAudio";
import UnauthorizedPage from "../pages/UnauthorizedPage";

// ---- route guards (legacy, unchanged) ----
import LoginRoute from "../../route/LoginRoute";
import PrivateRoute from "../../route/PrivateRoute";
import DashboardRoute from "../../route/DashboardRoute";

// ---- admin pages (legacy, unchanged — now mounted under /admin/*) ----
import Login from "../../adminPages/LoginPage/Login";
import ResetPassword from "../../adminPages/RestetPassword/ResetPassword";
import UserManagement from "../../adminPages/Dashboard/UserManagement";
import RoleManagement from "../../adminPages/Dashboard/RoleManagement";
import PermissionManagement from "../../adminPages/Dashboard/PermissionManagement";
import ClassManagement from "../../adminPages/Dashboard/ClassManagement";
import CategoriesManagement from "../../adminPages/Dashboard/CategoriesManagement";
import TagsManagement from "../../adminPages/Dashboard/TagsManagement";
import TagsTypeManagement from "../../adminPages/Dashboard/TagsTypeManagement";
import SeriesManagement from "../../adminPages/Dashboard/SeriesManagement";
import ContentManagement from "../../adminPages/Dashboard/ContentManagement";
import MediaLibrary from "../../adminPages/Dashboard/MediaLibrary";
import QuestionManagement from "../../adminPages/Dashboard/QuestionManagement";
import ContactUsManagement from "../../adminPages/Dashboard/ContactUsManagement";
import TeamManagement from "../../adminPages/Dashboard/TeamManagement";
import MenuManagement from "../../adminPages/Dashboard/MenuManagement";
import AdminSetting from "../../adminPages/Dashboard/AdminSetting";
import PageBuilderManagement from "../../adminPages/Dashboard/PageBuilderManagement";

// ---- redesign ----
import Styleguide from "../Styleguide";
import AuthPage from "../pages/AuthPage";
import AccountDashboardPage from "../pages/AccountDashboardPage";
import CategoriesIndexPage from "../pages/CategoriesIndexPage";
import TagPage from "../pages/TagPage";
import ShellPreview from "../shell/ShellPreview";

const guarded = (Guard, El, props = {}) => (
  <Guard {...props}>
    <El />
  </Guard>
);

/*
 * THE route manifest. Every static path in the app is declared here and only
 * here. The archive's class/category permalinks resolve through the trailing
 * catch-all — so a new static path silently shadows any archive content with
 * the same slug. Before adding one, check it can never collide with a content
 * permalink (backend slugs are lowercase words with hyphens; the safest new
 * namespaces are prefixed like /admin/*, /embed/*, /series/*).
 */
const publicRoutes = [
  { path: "/", element: <HomePage /> },
  // v2.2 auth flow — refreshed UI over the existing auth stack.
  { path: "/sign-in", element: guarded(LoginRoute, AuthPage, {}) },
  { path: "/sign-up", element: <LoginRoute><AuthPage initialMode="signup" /></LoginRoute> },
  { path: "/forget-password", element: <LoginRoute><AuthPage initialMode="reset" /></LoginRoute> },
  { path: "/contact-us", element: <ContactPage /> },
  { path: "/ask-question", element: <AskQuestionPage /> },
  { path: "/about-us", element: <AboutPage /> },
  { path: "/live", element: <LivePage /> },
  { path: "/donations", element: <DonationPage /> },
  { path: "/donations-mbl", element: <DonationPageMobile /> },
  { path: "/chai-club", element: <ChaiClubPage /> },
  { path: "/privacy-policy", element: <PrivacyPolicyPage /> },
  // Legacy search results page — its data only ever arrived via router state
  // from the old header, so a direct visit rendered blank. The redesigned
  // latest-classes feed is the nearest browsable surface.
  { path: "/view-all", element: <Navigate to="/latest-classes" replace /> },
  { path: "/daily-tanya", element: <DailyShiurPage kind="tanya" /> },
  { path: "/daily-hayom-yom", element: <DailyShiurPage kind="hayom-yom" /> },
  { path: "/weekly-parsha-classes", element: <ParshaPage /> },
  { path: "/monthly-classes", element: <MonthlyClassesPage /> },
  { path: "/latest-classes", element: <LatestClassesPage /> },
  // The account dashboard already shows bookmarks (and redirects signed-out
  // users itself), so the old standalone bookmarks page folds into it.
  { path: "/user-bookmarks", element: <Navigate to="/profile" replace /> },
  // v2.2 account dashboard (continue / bookmarks / liked).
  { path: "/profile", element: <AccountDashboardPage /> }, // page redirects signed-out users itself
  { path: "/categories", element: <CategoriesIndexPage /> },
  { path: "/series/:uniqueName", element: <SeriesPage /> },
  { path: "/tag/:uniqueName", element: <TagPage /> },
  { path: "/search", element: <SearchResultsPage /> },
  { path: "/concept/:slug", element: <ConceptPage /> },
  { path: "/unauthorized", element: <UnauthorizedPage /> },
  { path: "/embed/video/:classTitle", element: <EmbeddableVideo /> },
  { path: "/embed/audio/:classTitle", element: <EmbeddableAudio /> },
];

const adminGuarded = (El, permission) =>
  guarded(PrivateRoute, El, { requiredPermissions: permission });

const adminRoutes = [
  { path: "/admin", element: guarded(DashboardRoute, Login) },
  { path: "/admin/reset-password", element: <ResetPassword /> },
  { path: "/admin/user-management", element: adminGuarded(UserManagement, "view_user") },
  { path: "/admin/role-management", element: adminGuarded(RoleManagement, "view_role") },
  { path: "/admin/permission-management", element: adminGuarded(PermissionManagement, "view_permission") },
  { path: "/admin/class-management", element: adminGuarded(ClassManagement, "view_class") },
  { path: "/admin/class-management/categories", element: adminGuarded(CategoriesManagement, "view_category") },
  { path: "/admin/class-management/tags", element: adminGuarded(TagsManagement, "view_tag") },
  { path: "/admin/class-management/tags-type", element: adminGuarded(TagsTypeManagement, "view_tag") },
  { path: "/admin/class-management/series", element: adminGuarded(SeriesManagement, "view_series") },
  { path: "/admin/content-management", element: adminGuarded(ContentManagement, "view_slider") },
  { path: "/admin/content-management/media-library", element: adminGuarded(MediaLibrary, "view_gallery") },
  { path: "/admin/content-management/questions", element: adminGuarded(QuestionManagement, "view_question") },
  { path: "/admin/content-management/contact-us", element: adminGuarded(ContactUsManagement, "view_comment") },
  { path: "/admin/content-management/team", element: adminGuarded(TeamManagement, "view_team") },
  { path: "/admin/menu-management", element: adminGuarded(MenuManagement, "view_menu") },
  { path: "/admin/profile-setting", element: adminGuarded(AdminSetting, "view_menu") },
  { path: "/admin/page-builder", element: adminGuarded(PageBuilderManagement, "view_menu") },
];

// Old unprefixed admin bookmarks keep working.
const adminRedirects = LEGACY_ADMIN_REDIRECTS.map(({ from, to }) => ({
  path: from,
  element: <Navigate to={to} replace />,
}));

const devRoutes =
  process.env.NODE_ENV === "development"
    ? [
        { path: "/styleguide", element: <Styleguide /> },
        { path: "/shell-preview", element: <ShellPreview /> },
      ]
    : [];

const routes = [
  ...publicRoutes,
  ...adminRoutes,
  ...adminRedirects,
  ...devRoutes,
  // Archive permalink resolver — every class/category URL lands here.
  // Classes render the redesigned page; categories stay legacy until the
  // archive-view phase. NonSpecifiedRoute is retired.
  { path: "*", element: <PermalinkPage /> },
];

// Collision guard: two identical static paths in the manifest is always a bug.
if (process.env.NODE_ENV === "development") {
  const seen = new Set();
  for (const r of routes) {
    if (seen.has(r.path)) {
      throw new Error(`Duplicate route path in manifest: ${r.path}`);
    }
    seen.add(r.path);
  }
}

const router = createBrowserRouter([
  {
    element: <RootShell />,
    children: routes,
  },
]);

export default router;
